import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Messages.css';
import { useMessageContext } from '../context/MessageContext';

interface Thread {
  id: number;
  sender_id: number;
  recipient_id: number;
  content: string;
  is_read?: number;
  created_at: string;
  sender_username?: string;
  recipient_username?: string;
}

interface ConversationResponse {
  user: { id: number; username: string };
  messages: Thread[];
}

const Messages: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { refreshUnreadCount } = useMessageContext();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [conversation, setConversation] = useState<ConversationResponse | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    const fetchThreads = async () => {
      setLoadingThreads(true);
      try {
        const response = await api.get('/messages/inbox');
        setThreads(response.data.threads || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Fehler beim Laden der Nachrichten');
      } finally {
        setLoadingThreads(false);
      }
    };
    fetchThreads();
  }, []);

  useEffect(() => {
    const initialPartner = queryParams.get('user');
    if (initialPartner) {
      setSelectedUserId(parseInt(initialPartner, 10));
    } else if (threads.length > 0 && selectedUserId === null) {
      const otherUser =
        threads[0].sender_id === user?.id ? threads[0].recipient_id : threads[0].sender_id;
      setSelectedUserId(otherUser);
    }
  }, [threads, queryParams, user?.id, selectedUserId]);

  useEffect(() => {
    if (selectedUserId == null) {
      setConversation(null);
      return;
    }
    const fetchConversation = async () => {
      setConversationLoading(true);
      setError('');
      try {
        const response = await api.get(`/messages/conversation/${selectedUserId}`);
        setConversation(response.data);
        refreshUnreadCount();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Fehler beim Laden der Konversation');
        setConversation(null);
      } finally {
        setConversationLoading(false);
      }
    };
    fetchConversation();
  }, [selectedUserId, refreshUnreadCount]);

  const renderWithLinks = (content: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const parts = content.split(urlRegex);
    return parts.map((part, index) => {
      if (/^https?:\/\//i.test(part)) {
        return (
          <a
            key={`link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        );
      }
      const textLines = part.split('\n');
      return textLines.map((line, lineIndex) => (
        <React.Fragment key={`text-${index}-${lineIndex}`}>
          {line}
          {lineIndex < textLines.length - 1 && <br />}
        </React.Fragment>
      ));
    });
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!messageInput.trim() || selectedUserId == null) return;

    setSending(true);
    setError('');
    try {
      const response = await api.post('/messages', {
        recipient_id: selectedUserId,
        content: messageInput.trim(),
      });

      setMessageInput('');
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              messages: [...prev.messages, response.data.message],
            }
          : prev
      );

      setThreads((prev) => {
        const newMessage: Thread = response.data.message;
        const otherUserId =
          newMessage.sender_id === user?.id ? newMessage.recipient_id : newMessage.sender_id;
        const filtered = prev.filter((thread) => {
          const threadOtherUser =
            thread.sender_id === user?.id ? thread.recipient_id : thread.sender_id;
          return threadOtherUser !== otherUserId;
        });
        return [newMessage, ...filtered];
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Nachricht konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (selectedUserId == null) {
      return;
    }
    if (
      !window.confirm(
        'Möchtest du diese Unterhaltung wirklich löschen? Alle Nachrichten werden entfernt.'
      )
    ) {
      return;
    }
    try {
      await api.delete(`/messages/conversation/${selectedUserId}`);
      setConversation(null);
      setSelectedUserId(null);
      setThreads((prev) => prev.filter((thread) => {
        const otherId = thread.sender_id === user?.id ? thread.recipient_id : thread.sender_id;
        return otherId !== selectedUserId;
      }));
      refreshUnreadCount();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unterhaltung konnte nicht gelöscht werden');
    }
  };

  const renderThreadName = (thread: Thread) => {
    const otherUserId = thread.sender_id === user?.id ? thread.recipient_id : thread.sender_id;
    const name =
      thread.sender_id === user?.id ? thread.recipient_username : thread.sender_username;
    return { otherUserId, name: name ?? `User #${otherUserId}` };
  };

  return (
    <div className="messages-page">
      <div className="messages-layout">
        <aside className="thread-list">
          <h2>Konversationen</h2>
          {loadingThreads ? (
            <p className="muted-text">Konversationen werden geladen...</p>
          ) : threads.length === 0 ? (
            <p className="muted-text">Noch keine Nachrichten.</p>
          ) : (
            <ul>
              {threads.map((thread) => {
                const { otherUserId, name } = renderThreadName(thread);
                const isActive = selectedUserId === otherUserId;
                return (
                  <li key={`${thread.id}-${otherUserId}`}>
                    <button
                      type="button"
                      className={isActive ? 'thread-button active' : 'thread-button'}
                      onClick={() => setSelectedUserId(otherUserId)}
                    >
                      <span className="thread-name">{name}</span>
                      <span className="thread-preview">{thread.content}</span>
                      <span className="thread-date">
                        {new Date(thread.created_at).toLocaleDateString('de-DE')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className="conversation-panel">
          {error && <div className="error-banner">{error}</div>}

          {conversationLoading && <p className="muted-text">Nachrichten werden geladen...</p>}

          {!conversationLoading && conversation && (
            <>
              <header className="conversation-header">
                <div>
                  <p className="label">Unterhaltung mit</p>
                  <h2>
                    {conversation.user?.id &&
                    conversation.user.username?.trim().toLowerCase() !== 'system' ? (
                      <Link
                        to={`/profile/${conversation.user.id}`}
                        className="conversation-partner-link"
                      >
                        {conversation.user.username}
                      </Link>
                    ) : (
                      <span>{conversation.user.username}</span>
                    )}
                  </h2>
                </div>
                <button
                  type="button"
                  className="ghost-button danger"
                  onClick={() => handleDeleteConversation()}
                >
                  Unterhaltung löschen
                </button>
              </header>
              <div className="conversation-messages">
                {conversation.messages.length === 0 ? (
                  <p className="muted-text">Noch keine Nachrichten.</p>
                ) : (
                  conversation.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`message-bubble ${
                        message.sender_id === user?.id ? 'outgoing' : 'incoming'
                      }`}
                    >
                      <div className="message-meta">
                        <span>
                          {message.sender_id === user?.id
                            ? 'Du'
                            : message.sender_username ?? `User #${message.sender_id}`}{' '}
                        </span>
                        <span>{new Date(message.created_at).toLocaleString('de-DE')}</span>
                      </div>
                      <p>{renderWithLinks(message.content)}</p>
                    </div>
                  ))
                )}
              </div>

              <form className="message-form" onSubmit={handleSend}>
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Nachricht schreiben..."
                  maxLength={2000}
                  rows={3}
                />
                <button type="submit" disabled={!messageInput.trim() || sending}>
                  {sending ? 'Senden...' : 'Nachricht senden'}
                </button>
              </form>
            </>
          )}

          {!conversationLoading && !conversation && (
            <div className="empty-state">
              <p>Wähle einen Chat, um Nachrichten zu lesen.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Messages;

